import { useEffect } from "react";
import { useFieldArray } from "react-hook-form";
import { Plus, Trash } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Select } from "components/ui/Select/Select";
import { usePatientLongTermMedications } from "features/medications/hooks/usePatientLongTermMedications";
import { formatLongTermMedicationsToString } from "features/medications/utils/formatLongTermMedications";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";

import style from "../ReferralForm.module.css";

export const CurrentMedicationsAndConditionsFormSection = ({
  form,
  isLungReferral,
  isProstateReferral,
}) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "anticoagulantMedications",
  });

  const patientId = useCurrentPatientId();
  const { data: longTermMedications } =
    usePatientLongTermMedications(patientId);

  const handleImportMedications = () => {
    const text = formatLongTermMedicationsToString(longTermMedications ?? []);

    form.setValue("currentMedication", text, { shouldValidate: true });
  };

  useEffect(() => {
    if (fields.length === 0) {
      append({ name: "", dosage: "" });
    }
  }, [fields, append]);

  if (isLungReferral || isProstateReferral) {
    return null;
  }

  return (
    <div className={style.itemWrapper}>
      <div className={style.inputGroupGrid2Columns}>
        <div>
          <FormField name="patientOnAnticoagulants">
            <label className={style.addReferralFormLabel}>
              Patient on Anticoagulants <span>*</span>
            </label>
            <Select
              options={[
                {
                  label: "Yes",
                  value: "true",
                },
                {
                  label: "No",
                  value: "false",
                },
              ]}
              value={form.watch("patientOnAnticoagulants")}
              fullWidth
              onChange={(value) =>
                form.setValue("patientOnAnticoagulants", value)
              }
              required
            />
          </FormField>
          {form.watch("patientOnAnticoagulants") === "true" && (
            <div
              className={style.investigationSection}
              style={{ marginTop: "16px" }}
            >
              {fields.map((field, index) => (
                <div key={field.id} className={style.investigationItem}>
                  <div className={style.inputGroupGrid2Columns}>
                    <FormField name={`anticoagulantMedications.${index}.name`}>
                      <label className={style.addReferralFormLabel}>
                        Drug name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        {...form.register(
                          `anticoagulantMedications.${index}.name`
                        )}
                      />
                    </FormField>
                    <FormField
                      name={`anticoagulantMedications.${index}.dosage`}
                    >
                      <label className={style.addReferralFormLabel}>
                        Details (Dosage & Frequency)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        {...form.register(
                          `anticoagulantMedications.${index}.dosage`
                        )}
                      />
                    </FormField>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className={style.removeButton}
                    disabled={index === 0 && fields.length === 1}
                    aria-label="Remove anticoagulant medication"
                  >
                    <Trash width={18} height={18} />
                  </button>
                </div>
              ))}
              <button
                className={style.addInvestigationButton}
                type="button"
                onClick={() =>
                  append({ title: "", date: "", hospital: "", result: "" })
                }
              >
                {`Add another drug`}
                <Plus width={16} height={16} />
              </button>
            </div>
          )}
        </div>

        <FormField name="currentMedication">
          <div className={style.medicationsLabelRow}>
            <label className={style.addReferralFormLabel}>
              Current Medication
            </label>
            <button
              type="button"
              className={style.importButton}
              disabled={!longTermMedications?.length}
              onClick={handleImportMedications}
            >
              Import from patient file
            </button>
          </div>
          <textarea
            className="form-control textareaform-control"
            style={{ minHeight: "126px" }}
            {...form.register("currentMedication")}
          />
        </FormField>
      </div>
    </div>
  );
};
